/**
 * useMailingAddress - Hook for managing mailing address state
 */

import { useState, useEffect, useCallback } from 'react'
import {
  getAddress,
  submitAddress,
  updateAddress,
  type MailingAddress,
  type AddressData,
  type SubmitResult
} from './mailingService'

export function useMailingAddress() {
  const [address, setAddress] = useState<MailingAddress | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAddress = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getAddress()
      setAddress(data)
    } catch (err) {
      console.error('Failed to fetch mailing address:', err)
      setError('Failed to load address')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAddress()
  }, [fetchAddress])

  const submit = useCallback(async (data: AddressData): Promise<SubmitResult> => {
    setError(null)
    const result = await submitAddress(data)
    if (result.success && result.address) {
      setAddress(result.address)
    }
    return result
  }, [])

  const update = useCallback(async (data: AddressData): Promise<SubmitResult> => {
    setError(null)
    const result = await updateAddress(data)
    if (result.success && result.address) {
      setAddress(result.address)
    }
    return result
  }, [])

  return {
    address,
    hasAddress: !!address,
    isLoading,
    error,
    submit,
    update,
    refresh: fetchAddress
  }
}
